import { useEffect, useState } from "react"
import * as Sentry from "@sentry/react"

import type { RemoteData } from "../utils/remotedata"
import { ApiError } from "../api/utils"

const useFetch = <T>(
  fetcher: (baseUrl: string, token: string, extraParam?: string) => Promise<T>,
  baseUrl: string,
  token: string,
  extraParam?: string,
): RemoteData<Error, T> => {
  const [req, setReq] = useState<RemoteData<Error, T>>({
    type: "NotAsked",
  })

  useEffect(() => {
    const go = async () => {
      setReq({ type: "Loading" })

      try {
        const data =
          extraParam !== undefined
            ? await fetcher(baseUrl, token, extraParam)
            : await fetcher(baseUrl, token)
        setReq({ type: "Success", data })
      } catch (e) {
        if (e instanceof Error) {
          Sentry.withScope((scope) => {
            scope.setTag("hook", "useFetch")
            if (extraParam) {
              scope.setExtra("extraParam", extraParam)
            }
            if (e instanceof ApiError) {
              scope.setTag("operation", e.operation)
              scope.setTag("statusCode", e.statusCode.toString())
              scope.setExtras(e.context)
            }
            Sentry.captureException(e)
          })
          setReq({ type: "Failure", error: e })
        }
      }
    }

    go().catch((error) => {
      Sentry.withScope((scope) => {
        scope.setTag("hook", "useFetch")
        scope.setTag("errorLocation", "unhandledPromise")
        Sentry.captureException(error)
      })
    })
  }, [fetcher, baseUrl, token, extraParam])

  return req
}

export default useFetch
